/**
 * Topic operations.
 *
 * Two resolvers live here, and they are **not** the same function. `findOrCreateTopic` skips
 * archived topics; `findOrCreateGenreTopics`, which came from the media hooks, does not. Unifying
 * them changes what media genre matching finds, so it is stated here rather than folded in silently
 * — `docs/target-structure.md` names it as one of the restructure's two behaviour changes and it
 * gets its own commit.
 */

import { normalizeTopicName } from '@/utils/topic-utils'

import { newId } from '../ids'
import type { AppDocument, EntryType } from '../schema'

/**
 * The id of the topic with this name for this entry type, creating it if there is none.
 *
 * Matches on the normalised name and keeps the caller's spelling on a topic it creates. Returns
 * `null` for a blank name.
 */
export function findOrCreateTopic(
  doc: AppDocument,
  name: string,
  entryType: EntryType | null,
): string | null {
  const trimmed = name.trim()
  if (!trimmed) return null

  const normalized = normalizeTopicName(trimmed)

  const existing = Object.values(doc.topics).find((topic) => {
    if (topic.archivedAt) return false
    if (topic.entryType !== entryType) return false
    return normalizeTopicName(topic.name) === normalized
  })

  if (existing) return existing.id

  const id = newId()
  doc.topics[id] = {
    id,
    name: trimmed,
    description: '',
    parentId: null,
    entryType,
    isDefault: false,
  }

  return id
}

/**
 * Topic ids for a list of genre names, creating the ones that do not exist.
 *
 * The media path's resolver. It differs from `findOrCreateTopic` in two ways it inherited and keeps:
 * it does not skip archived topics, and it capitalises the first letter of a topic it creates.
 */
export function findOrCreateGenreTopics(
  doc: AppDocument,
  genres: readonly string[],
  scope: EntryType | 'album',
): string[] {
  return genres.reduce<string[]>((ids, genre) => {
    const trimmed = genre.trim()
    if (!trimmed) return ids

    const normalized = normalizeTopicName(trimmed)
    const existing = Object.values(doc.topics).find(
      (topic) =>
        topic.entryType === scope &&
        normalizeTopicName(topic.name) === normalized,
    )

    if (existing) {
      ids.push(existing.id)
      return ids
    }

    const id = newId()
    doc.topics[id] = {
      id,
      name: trimmed.charAt(0).toUpperCase() + trimmed.slice(1),
      description: '',
      parentId: null,
      entryType: scope as EntryType,
      isDefault: false,
    }
    ids.push(id)
    return ids
  }, [])
}
